import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { companyAPI, statementAPI, insightsAPI, dashboardAPI, documentAPI, reportAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  House,
  ArrowLeft,
  Plus,
  Sparkle,
  TrendUp,
  TrendDown,
  Bank,
  CurrencyDollar,
  Receipt,
  CaretDown,
  User,
  SignOut,
  Gear,
  CreditCard,
  Spinner,
  FileText,
  Upload,
  Trash,
  MagnifyingGlass,
  CheckCircle,
  DownloadSimple,
  FilePdf,
  FileXls,
  FileCsv,
  List,
  Buildings,
  ChartLineUp,
  CaretRight,
  Question
} from '@phosphor-icons/react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import AddStatementModal from '../components/AddStatementModal';
import UploadDocumentModal from '../components/UploadDocumentModal';

// Logo component
const FinanciALLogo = () => (
  <img 
    src="https://customer-assets-0z36b82j.emergentagent.net/job_1ce9899f-22e9-4951-9d4b-a91c40afd391/artifacts/ktwxigwh_Liquid%20Glass%20Logo.png.png" 
    alt="FinanciAL" 
    className="w-8 h-8 rounded-lg object-cover"
  />
);

const formatCurrency = (value, currency = 'USD') => {
  if (!value && value !== 0) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatPercent = (value) => {
  if (!value && value !== 0) return '0%';
  return `${value.toFixed(1)}%`;
};

// Sidebar Navigation Item
const NavItem = ({ icon: Icon, label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
      active 
        ? 'bg-[#1A3C34] text-white' 
        : 'text-[#1A3C34]/70 hover:bg-[#1A3C34]/5 hover:text-[#1A3C34]'
    }`}
  >
    <Icon className="w-5 h-5" weight={active ? 'fill' : 'regular'} />
    <span className="flex-1 text-left">{label}</span>
  </button>
);

// Stat Card - Clean minimal design
const StatCard = ({ label, value, change, icon: Icon, color = 'default' }) => {
  const colorClasses = {
    default: 'bg-[#1A3C34]/5 text-[#1A3C34]',
    success: 'bg-[#2A9D8F]/10 text-[#2A9D8F]',
    warning: 'bg-[#D4A373]/10 text-[#D4A373]',
    danger: 'bg-[#E76F51]/10 text-[#E76F51]'
  };

  return (
    <div className="bg-white rounded-xl p-5 border border-[#1A3C34]/5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[#1A3C34]/60 text-sm">{label}</span>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-4 h-4" weight="duotone" />
        </div>
      </div>
      <div className="flex items-end justify-between">
        <span className="text-2xl font-semibold text-[#1A3C34] font-mono">{value}</span>
        {change !== undefined && (
          <span className={`text-xs font-medium flex items-center gap-0.5 ${
            change >= 0 ? 'text-[#2A9D8F]' : 'text-[#E76F51]'
          }`}>
            {change >= 0 ? <TrendUp className="w-3 h-3" /> : <TrendDown className="w-3 h-3" />}
            {formatPercent(Math.abs(change))}
          </span>
        )}
      </div>
    </div>
  );
};

const CompanyDetail = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [statements, setStatements] = useState([]);
  const [insights, setInsights] = useState([]);
  const [trends, setTrends] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [documentAnalyses, setDocumentAnalyses] = useState([]);
  const [generatingInsight, setGeneratingInsight] = useState(false);
  const [analyzingDocuments, setAnalyzingDocuments] = useState(false);
  const [showAddStatement, setShowAddStatement] = useState(false);
  const [showUploadDocument, setShowUploadDocument] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [companyForm, setCompanyForm] = useState({ name: '', industry: '', fiscal_year_end: 'December', currency: 'USD', tax_id: '', description: '' });
  const [savingCompany, setSavingCompany] = useState(false);
  const [deletingCompany, setDeletingCompany] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    fetchData();
  }, [companyId]);

  const fetchData = async () => {
    try {
      const [companyRes, statementsRes, insightsRes, trendsRes, docsRes, analysesRes] = await Promise.all([
        companyAPI.get(companyId),
        statementAPI.getAll(companyId),
        insightsAPI.getAll(companyId),
        dashboardAPI.getTrends(companyId),
        documentAPI.getAll(companyId).catch(() => ({ data: [] })),
        documentAPI.getAnalyses(companyId).catch(() => ({ data: [] }))
      ]);
      
      setCompany(companyRes.data);
      setCompanyForm({
        name: companyRes.data.name || '',
        industry: companyRes.data.industry || '',
        fiscal_year_end: companyRes.data.fiscal_year_end || 'December',
        currency: companyRes.data.currency || 'USD',
        tax_id: companyRes.data.tax_id || '',
        description: companyRes.data.description || '',
      });
      setStatements(statementsRes.data);
      setInsights(insightsRes.data);
      setTrends(trendsRes.data);
      setDocuments(docsRes.data || []);
      setDocumentAnalyses(analysesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Dështoi ngarkimi i të dhënave');
      if (error.response?.status === 404) {
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInsight = async () => {
    setGeneratingInsight(true);
    try {
      const response = await insightsAPI.generate(companyId, 'comprehensive');
      setInsights([response.data, ...insights]);
      toast.success('Njohuritë AI u gjeneruan me sukses!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dështoi gjenerimi i njohurive');
    } finally {
      setGeneratingInsight(false);
    }
  };

  const handleDocumentUploaded = () => {
    fetchData();
    setShowUploadDocument(false);
    toast.success('Dokumenti u ngarkua!');
  };

  const handleAnalyzeDocuments = async () => {
    if (documents.length === 0) {
      toast.error('Ju lutem ngarkoni disa dokumente së pari');
      return;
    }
    
    setAnalyzingDocuments(true);
    try {
      const response = await documentAPI.analyze(companyId, 'comprehensive');
      setDocumentAnalyses([response.data, ...documentAnalyses]);
      toast.success('Dokumentet u analizuan me sukses!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dështoi analizimi');
    } finally {
      setAnalyzingDocuments(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      await documentAPI.delete(companyId, documentId);
      setDocuments(documents.filter(d => d.document_id !== documentId));
      toast.success('Dokumenti u fshi');
    } catch (error) {
      toast.error('Dështoi fshirja');
    }
  };

  const handleStatementAdded = () => {
    fetchData();
    setShowAddStatement(false);
    toast.success('Pasqyra u shtua!');
  };

  const handleSaveCompany = async () => {
    if (!companyForm.name.trim() || !companyForm.industry.trim()) {
      toast.error('Emri dhe industria janë të detyrueshme');
      return;
    }
    setSavingCompany(true);
    try {
      const res = await companyAPI.update(companyId, {
        name: companyForm.name.trim(),
        industry: companyForm.industry.trim(),
        fiscal_year_end: companyForm.fiscal_year_end,
        currency: companyForm.currency,
        tax_id: companyForm.tax_id || null,
        description: companyForm.description || null,
      });
      setCompany(res.data);
      toast.success('Kompania u përditësua me sukses!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dështoi përditësimi i kompanisë');
    } finally {
      setSavingCompany(false);
    }
  };

  const handleDeleteCompany = async () => {
    setDeletingCompany(true);
    try {
      await companyAPI.delete(companyId);
      toast.success('Kompania u fshi me sukses');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dështoi fshirja e kompanisë');
      setDeletingCompany(false);
    }
  };

  const handleExport = async (format) => {
    try {
      toast.info(`Duke gjeneruar ${format.toUpperCase()}...`);
      
      let response;
      if (format === 'excel') response = await reportAPI.exportExcel(companyId);
      else if (format === 'pdf') response = await reportAPI.exportPDF(companyId);
      else response = await reportAPI.exportCSV(companyId);
      
      const blob = new Blob([response.data], { 
        type: format === 'excel' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : format === 'pdf' ? 'application/pdf' : 'text/csv'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${company?.name?.replace(/\s+/g, '_')}_Raport.${format === 'excel' ? 'xlsx' : format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`${format.toUpperCase()} u shkarkua!`);
    } catch (error) {
      toast.error(`Dështoi eksportimi`);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const latestStatement = statements[0];
  const chartData = trends?.periods?.map((period, i) => ({
    period,
    revenue: trends.revenue[i] || 0,
    profit: trends.net_income[i] || 0
  })) || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="text-center">
          <Spinner className="w-8 h-8 text-[#1A3C34] animate-spin mx-auto mb-3" />
          <p className="text-[#1A3C34]/60 text-sm">Duke ngarkuar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex">
      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-full bg-white border-r border-[#1A3C34]/5 z-50 transition-all duration-300 ${
        sidebarCollapsed ? 'w-16' : 'w-64'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-[#1A3C34]/5">
            <Link to="/dashboard" className="flex items-center gap-3">
              <FinanciALLogo />
              {!sidebarCollapsed && (
                <span className="font-semibold text-[#1A3C34]">FinanciAL</span>
              )}
            </Link>
          </div>

          {/* Company Info */}
          {!sidebarCollapsed && (
            <div className="p-4 border-b border-[#1A3C34]/5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#1A3C34] to-[#2A9D8F] flex items-center justify-center text-white font-semibold">
                  {company?.name?.charAt(0) || 'K'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#1A3C34] truncate text-sm">{company?.name}</p>
                  <p className="text-xs text-[#1A3C34]/50">{company?.industry}</p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {!sidebarCollapsed && (
              <>
                <NavItem 
                  icon={House} 
                  label="Përmbledhje" 
                  active={activeTab === 'overview'}
                  onClick={() => setActiveTab('overview')}
                />
                <NavItem 
                  icon={Receipt} 
                  label="Pasqyrat" 
                  active={activeTab === 'statements'}
                  onClick={() => setActiveTab('statements')}
                />
                <NavItem 
                  icon={FileText} 
                  label="Dokumentet" 
                  active={activeTab === 'documents'}
                  onClick={() => setActiveTab('documents')}
                />
                <NavItem 
                  icon={Sparkle} 
                  label="Njohuri AI" 
                  active={activeTab === 'insights'}
                  onClick={() => setActiveTab('insights')}
                />
                <NavItem 
                  icon={ChartLineUp} 
                  label="Treguesit" 
                  active={activeTab === 'ratios'}
                  onClick={() => setActiveTab('ratios')}
                />
                <NavItem 
                  icon={Gear} 
                  label="Cilësimet" 
                  active={activeTab === 'settings'}
                  onClick={() => setActiveTab('settings')}
                />
                
                <div className="pt-4 mt-4 border-t border-[#1A3C34]/5">
                  <NavItem 
                    icon={ArrowLeft} 
                    label="Kthehu te Paneli" 
                    active={false}
                    onClick={() => navigate('/dashboard')}
                  />
                </div>
              </>
            )}
          </nav>

          {/* User Section */}
          {!sidebarCollapsed && (
            <div className="p-3 border-t border-[#1A3C34]/5">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-[#1A3C34]/5 transition-colors">
                    <div className="w-8 h-8 rounded-full bg-[#1A3C34] flex items-center justify-center">
                      {user?.picture ? (
                        <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                      ) : (
                        <User className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-sm font-medium text-[#1A3C34] truncate">{user?.name}</p>
                    </div>
                    <CaretDown className="w-4 h-4 text-[#1A3C34]/40" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => navigate('/billing')}>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Faturimi
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <SignOut className="w-4 h-4 mr-2" />
                    Dil
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-64'}`}>
        {/* Top Header */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-sm border-b border-[#1A3C34]/5">
          <div className="flex items-center justify-between h-14 px-6">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="p-2 rounded-lg hover:bg-[#1A3C34]/5 transition-colors"
              >
                <List className="w-5 h-5 text-[#1A3C34]/60" />
              </button>
              <h1 className="text-lg font-semibold text-[#1A3C34]">
                {activeTab === 'overview' && 'Përmbledhje'}
                {activeTab === 'statements' && 'Pasqyrat Financiare'}
                {activeTab === 'documents' && 'Dokumentet'}
                {activeTab === 'insights' && 'Njohuri AI'}
                {activeTab === 'ratios' && 'Treguesit Financiarë'}
                {activeTab === 'settings' && 'Cilësimet e Kompanisë'}
              </h1>
            </div>

            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="border-[#1A3C34]/20">
                    <DownloadSimple className="w-4 h-4 mr-1.5" />
                    Eksporto
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleExport('excel')}>
                    <FileXls className="w-4 h-4 mr-2 text-[#2A9D8F]" />
                    Excel
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('pdf')}>
                    <FilePdf className="w-4 h-4 mr-2 text-[#E76F51]" />
                    PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('csv')}>
                    <FileCsv className="w-4 h-4 mr-2 text-[#1A3C34]" />
                    CSV
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {activeTab === 'statements' && (
                <Button
                  onClick={() => setShowAddStatement(true)}
                  className="bg-[#1A3C34] hover:bg-[#1A3C34]/90 text-white"
                  size="sm"
                >
                  <Plus className="w-4 h-4 mr-1.5" />
                  Shto Pasqyrë
                </Button>
              )}

              {activeTab === 'documents' && (
                <Button
                  onClick={() => setShowUploadDocument(true)}
                  className="bg-[#1A3C34] hover:bg-[#1A3C34]/90 text-white"
                  size="sm"
                >
                  <Upload className="w-4 h-4 mr-1.5" />
                  Ngarko
                </Button>
              )}

              {activeTab === 'insights' && (
                <Button
                  onClick={handleGenerateInsight}
                  className="bg-[#D4A373] hover:bg-[#D4A373]/90 text-white"
                  size="sm"
                  disabled={generatingInsight || statements.length === 0}
                >
                  {generatingInsight ? (
                    <Spinner className="w-4 h-4 mr-1.5 animate-spin" />
                  ) : (
                    <Sparkle className="w-4 h-4 mr-1.5" />
                  )}
                  Gjenero
                </Button>
              )}
            </div>
          </div>
        </header>

        {/* Content Area */}
        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  label="Të Ardhura"
                  value={formatCurrency(latestStatement?.income_statement?.revenue || 0)}
                  icon={CurrencyDollar}
                  color="default"
                />
                <StatCard
                  label="Fitimi Neto"
                  value={formatCurrency(latestStatement?.income_statement?.net_income || 0)}
                  change={latestStatement?.financial_ratios?.net_margin}
                  icon={TrendUp}
                  color={(latestStatement?.income_statement?.net_income || 0) >= 0 ? 'success' : 'danger'}
                />
                <StatCard
                  label="Asete Totale"
                  value={formatCurrency(latestStatement?.balance_sheet?.total_assets || 0)}
                  icon={Bank}
                  color="warning"
                />
                <StatCard
                  label="Marzha Neto"
                  value={formatPercent(latestStatement?.financial_ratios?.net_margin || 0)}
                  icon={Receipt}
                  color="default"
                />
              </div>

              {/* Charts Row */}
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Revenue Trend */}
                <div className="bg-white rounded-xl border border-[#1A3C34]/5 p-5">
                  <h3 className="font-semibold text-[#1A3C34] mb-4">Trendi i të Ardhurave</h3>
                  {chartData.length > 0 ? (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData}>
                          <defs>
                            <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#1A3C34" stopOpacity={0.1}/>
                              <stop offset="95%" stopColor="#1A3C34" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#2A9D8F" stopOpacity={0.1}/>
                              <stop offset="95%" stopColor="#2A9D8F" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E0" vertical={false} />
                          <XAxis dataKey="period" tick={{ fill: '#1A3C34', fontSize: 10 }} tickLine={false} axisLine={false} />
                          <YAxis tick={{ fill: '#1A3C34', fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} />
                          <Tooltip formatter={(value) => [formatCurrency(value), '']} />
                          <Area type="monotone" dataKey="revenue" stroke="#1A3C34" strokeWidth={2} fill="url(#colorRev)" name="Të Ardhura" />
                          <Area type="monotone" dataKey="profit" stroke="#2A9D8F" strokeWidth={2} fill="url(#colorProfit)" name="Fitimi" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-[#1A3C34]/40">
                      Nuk ka të dhëna
                    </div>
                  )}
                </div>

                {/* Key Ratios */}
                <div className="bg-white rounded-xl border border-[#1A3C34]/5 p-5">
                  <h3 className="font-semibold text-[#1A3C34] mb-4">Raportet Kryesore</h3>
                  <div className="space-y-4">
                    {[
                      { label: 'Marzha Bruto', value: latestStatement?.financial_ratios?.gross_margin, target: 40 },
                      { label: 'Marzha Neto', value: latestStatement?.financial_ratios?.net_margin, target: 15 },
                      { label: 'Raporti Korrent', value: latestStatement?.financial_ratios?.current_ratio, target: 2, isRatio: true },
                      { label: 'Kthimi mbi Asetet', value: latestStatement?.financial_ratios?.return_on_assets, target: 10 },
                      { label: 'Kthimi mbi Kapitalin', value: latestStatement?.financial_ratios?.return_on_equity, target: 15 }
                    ].map((ratio, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-[#1A3C34]/5 last:border-0">
                        <span className="text-sm text-[#1A3C34]/70">{ratio.label}</span>
                        <span className={`font-mono text-sm font-medium ${
                          (ratio.value || 0) >= ratio.target ? 'text-[#2A9D8F]' : 'text-[#1A3C34]'
                        }`}>
                          {ratio.isRatio ? (ratio.value?.toFixed(2) || '0.00') : formatPercent(ratio.value || 0)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Balance Sheet Summary */}
              <div className="bg-white rounded-xl border border-[#1A3C34]/5 p-5">
                <h3 className="font-semibold text-[#1A3C34] mb-4">Përmbledhja e Bilancit</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-[#1A3C34]/60 mb-3">Asetet</h4>
                    <div className="space-y-2">
                      {[
                        { label: 'Paraja', value: latestStatement?.balance_sheet?.cash },
                        { label: 'Llogaritë e Arkëtueshme', value: latestStatement?.balance_sheet?.accounts_receivable },
                        { label: 'Inventari', value: latestStatement?.balance_sheet?.inventory },
                      ].map((item, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-[#1A3C34]/60">{item.label}</span>
                          <span className="font-mono text-[#1A3C34]">{formatCurrency(item.value || 0)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between text-sm font-medium pt-2 border-t border-[#1A3C34]/10">
                        <span className="text-[#1A3C34]">Total</span>
                        <span className="font-mono text-[#1A3C34]">{formatCurrency(latestStatement?.balance_sheet?.total_assets || 0)}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-[#1A3C34]/60 mb-3">Detyrimet</h4>
                    <div className="space-y-2">
                      {[
                        { label: 'Llogaritë e Pagueshme', value: latestStatement?.balance_sheet?.accounts_payable },
                        { label: 'Borxhi Afatshkurtër', value: latestStatement?.balance_sheet?.short_term_debt },
                        { label: 'Borxhi Afatgjatë', value: latestStatement?.balance_sheet?.long_term_debt },
                      ].map((item, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-[#1A3C34]/60">{item.label}</span>
                          <span className="font-mono text-[#1A3C34]">{formatCurrency(item.value || 0)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between text-sm font-medium pt-2 border-t border-[#1A3C34]/10">
                        <span className="text-[#1A3C34]">Total</span>
                        <span className="font-mono text-[#1A3C34]">{formatCurrency(latestStatement?.balance_sheet?.total_liabilities || 0)}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-[#1A3C34]/60 mb-3">Kapitali</h4>
                    <div className="space-y-2">
                      {[
                        { label: 'Kapitali i Aksionarëve', value: latestStatement?.balance_sheet?.shareholders_equity },
                        { label: 'Fitimet e Mbajtura', value: latestStatement?.balance_sheet?.retained_earnings },
                      ].map((item, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-[#1A3C34]/60">{item.label}</span>
                          <span className="font-mono text-[#1A3C34]">{formatCurrency(item.value || 0)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between text-sm font-medium pt-2 border-t border-[#1A3C34]/10">
                        <span className="text-[#1A3C34]">Total</span>
                        <span className="font-mono text-[#1A3C34]">{formatCurrency(latestStatement?.balance_sheet?.total_equity || 0)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Statements Tab */}
          {activeTab === 'statements' && (
            <div className="bg-white rounded-xl border border-[#1A3C34]/5">
              {statements.length > 0 ? (
                <div className="divide-y divide-[#1A3C34]/5">
                  {statements.map((stmt, index) => (
                    <motion.div
                      key={stmt.statement_id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="p-4 hover:bg-[#F8F9FA] transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-[#1A3C34]/5 flex items-center justify-center">
                            <Receipt className="w-5 h-5 text-[#1A3C34]" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-[#1A3C34]">{stmt.period}</span>
                              <Badge variant="outline" className="text-xs">{stmt.period_type}</Badge>
                            </div>
                            <p className="text-sm text-[#1A3C34]/60">
                              Të Ardhura: {formatCurrency(stmt.income_statement.revenue)} • Fitimi: {formatCurrency(stmt.income_statement.net_income)}
                            </p>
                          </div>
                        </div>
                        <span className={`font-mono text-sm font-medium ${
                          stmt.financial_ratios.net_margin >= 0 ? 'text-[#2A9D8F]' : 'text-[#E76F51]'
                        }`}>
                          {formatPercent(stmt.financial_ratios.net_margin)} marzhë
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16">
                  <Receipt className="w-12 h-12 text-[#1A3C34]/20 mb-4" />
                  <h3 className="font-medium text-[#1A3C34] mb-2">Ende pa pasqyra</h3>
                  <p className="text-sm text-[#1A3C34]/60 mb-4">Shtoni pasqyrën tuaj të parë</p>
                  <Button onClick={() => setShowAddStatement(true)} className="bg-[#1A3C34] text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Shto Pasqyrë
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="space-y-6">
              {/* Action Bar */}
              <div className="flex items-center justify-between">
                <p className="text-sm text-[#1A3C34]/60">
                  {documents.length} dokumente të ngarkuara
                </p>
                {documents.length > 0 && (
                  <Button
                    onClick={handleAnalyzeDocuments}
                    variant="outline"
                    size="sm"
                    className="border-[#2A9D8F] text-[#2A9D8F]"
                    disabled={analyzingDocuments}
                  >
                    {analyzingDocuments ? (
                      <Spinner className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <MagnifyingGlass className="w-4 h-4 mr-2" />
                    )}
                    Analizo të Gjitha
                  </Button>
                )}
              </div>

              {/* Documents List */}
              <div className="bg-white rounded-xl border border-[#1A3C34]/5">
                {documents.length > 0 ? (
                  <div className="divide-y divide-[#1A3C34]/5">
                    {documents.map((doc, index) => (
                      <div key={doc.document_id} className="p-4 flex items-center justify-between hover:bg-[#F8F9FA]">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-[#1A3C34]/5 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-[#1A3C34]" />
                          </div>
                          <div>
                            <p className="font-medium text-[#1A3C34]">{doc.filename}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">{doc.category}</Badge>
                              {doc.extracted_data?.total_amount && (
                                <span className="text-sm font-mono text-[#2A9D8F]">
                                  ${doc.extracted_data.total_amount.toLocaleString()}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteDocument(doc.document_id)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16">
                    <FileText className="w-12 h-12 text-[#1A3C34]/20 mb-4" />
                    <h3 className="font-medium text-[#1A3C34] mb-2">Nuk ka dokumente</h3>
                    <p className="text-sm text-[#1A3C34]/60 mb-4">Ngarkoni faturat për analizë AI</p>
                    <Button onClick={() => setShowUploadDocument(true)} className="bg-[#1A3C34] text-white">
                      <Upload className="w-4 h-4 mr-2" />
                      Ngarko Dokument
                    </Button>
                  </div>
                )}
              </div>

              {/* Analysis Results */}
              {documentAnalyses.length > 0 && (
                <div className="bg-[#2A9D8F]/5 rounded-xl border border-[#2A9D8F]/20 p-5">
                  <h3 className="font-semibold text-[#1A3C34] mb-4 flex items-center gap-2">
                    <MagnifyingGlass className="w-5 h-5 text-[#2A9D8F]" />
                    Rezultatet e Analizës
                  </h3>
                  {documentAnalyses.map((analysis) => (
                    <div key={analysis.analysis_id} className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="bg-white rounded-lg p-4">
                          <p className="text-xs text-[#1A3C34]/60 mb-1">Shpenzimet Totale</p>
                          <p className="font-mono text-lg font-semibold text-[#1A3C34]">
                            ${analysis.total_expenses?.toLocaleString() || '0'}
                          </p>
                        </div>
                        <div className="bg-white rounded-lg p-4">
                          <p className="text-xs text-[#1A3C34]/60 mb-1">Tatimi i Vlerësuar</p>
                          <p className="font-mono text-lg font-semibold text-[#E76F51]">
                            ${analysis.tax_obligations?.estimated_tax_due?.toLocaleString() || '0'}
                          </p>
                        </div>
                        <div className="bg-white rounded-lg p-4">
                          <p className="text-xs text-[#1A3C34]/60 mb-1">Shuma e Zbritshme</p>
                          <p className="font-mono text-lg font-semibold text-[#2A9D8F]">
                            ${analysis.tax_obligations?.tax_deductible_amount?.toLocaleString() || '0'}
                          </p>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-4">
                        <h4 className="font-medium text-[#1A3C34] mb-2">Analiza AI</h4>
                        <p className="text-sm text-[#1A3C34]/70 whitespace-pre-wrap">{analysis.insights}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Insights Tab */}
          {activeTab === 'insights' && (
            <div className="space-y-4">
              {insights.length > 0 ? (
                insights.map((insight, index) => (
                  <motion.div
                    key={insight.insight_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-xl border border-[#D4A373]/20 p-5"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkle className="w-5 h-5 text-[#D4A373]" weight="fill" />
                      <span className="font-medium text-[#1A3C34]">
                        Analiza {insight.analysis_type}
                      </span>
                      <span className="text-xs text-[#1A3C34]/50 ml-auto">
                        {new Date(insight.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-[#1A3C34]/70 whitespace-pre-wrap">{insight.insights}</p>
                  </motion.div>
                ))
              ) : (
                <div className="bg-white rounded-xl border border-[#1A3C34]/5 flex flex-col items-center justify-center py-16">
                  <Sparkle className="w-12 h-12 text-[#D4A373]/30 mb-4" />
                  <h3 className="font-medium text-[#1A3C34] mb-2">Ende pa njohuri</h3>
                  <p className="text-sm text-[#1A3C34]/60 mb-4 text-center max-w-sm">
                    {statements.length > 0 
                      ? 'Gjeneroni njohuri AI për të analizuar të dhënat financiare'
                      : 'Shtoni pasqyrat financiare për të gjeneruar njohuri'}
                  </p>
                  {statements.length > 0 && (
                    <Button
                      onClick={handleGenerateInsight}
                      className="bg-[#D4A373] hover:bg-[#D4A373]/90 text-white"
                      disabled={generatingInsight}
                    >
                      {generatingInsight ? <Spinner className="w-4 h-4 mr-2 animate-spin" /> : <Sparkle className="w-4 h-4 mr-2" />}
                      Gjenero Njohuri
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Financial Ratios Tab */}
          {activeTab === 'ratios' && (() => {
            const latest = statements.find(s => s.financial_ratios && Object.keys(s.financial_ratios).length > 0);
            const r = latest?.financial_ratios || {};
            const groups = [
              {
                title: 'Përfitueshmëria',
                color: '#2A9D8F',
                items: [
                  { key: 'gross_margin', label: 'Marzhi Bruto', suffix: '%' },
                  { key: 'operating_margin', label: 'Marzhi Operativ', suffix: '%' },
                  { key: 'net_margin', label: 'Marzhi Neto', suffix: '%' },
                  { key: 'return_on_assets', label: 'Kthimi nga Asetet (ROA)', suffix: '%' },
                  { key: 'return_on_equity', label: 'Kthimi nga Kapitali (ROE)', suffix: '%' },
                ],
              },
              {
                title: 'Likuiditeti',
                color: '#D4A373',
                items: [
                  { key: 'current_ratio', label: 'Raporti Rrjedhës', suffix: '' },
                  { key: 'quick_ratio', label: 'Raporti i Shpejtë', suffix: '' },
                ],
              },
              {
                title: 'Leva Financiare',
                color: '#E76F51',
                items: [
                  { key: 'debt_to_equity', label: 'Borxhi ndaj Kapitalit', suffix: '' },
                  { key: 'debt_ratio', label: 'Raporti i Borxhit', suffix: '' },
                ],
              },
            ];
            if (!latest) {
              return (
                <div className="bg-white rounded-xl border border-[#1A3C34]/5 flex flex-col items-center justify-center py-16">
                  <ChartLineUp className="w-12 h-12 text-[#1A3C34]/20 mb-4" />
                  <h3 className="font-medium text-[#1A3C34] mb-2">Ende pa tregues</h3>
                  <p className="text-sm text-[#1A3C34]/60 mb-4 text-center max-w-sm">Shtoni një pasqyrë financiare për të llogaritur treguesit financiarë automatikisht.</p>
                  <Button onClick={() => setActiveTab('statements')} className="bg-[#1A3C34] text-white">
                    <Plus className="w-4 h-4 mr-2" />Shto Pasqyrë
                  </Button>
                </div>
              );
            }
            return (
              <div className="space-y-6" data-testid="company-ratios-tab">
                <p className="text-sm text-[#1A3C34]/60">Bazuar në pasqyrën më të fundit: <span className="font-medium text-[#1A3C34]">{latest.period}</span></p>
                {groups.map(group => (
                  <div key={group.title}>
                    <h3 className="font-semibold text-[#1A3C34] mb-3">{group.title}</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                      {group.items.map(item => {
                        const val = r[item.key];
                        return (
                          <div key={item.key} className="bg-white rounded-xl border border-[#1A3C34]/5 p-4">
                            <p className="text-xs text-[#1A3C34]/60 mb-2 h-8">{item.label}</p>
                            <p className="text-2xl font-semibold font-mono" style={{ color: group.color }}>
                              {val !== undefined && val !== null ? `${val}${item.suffix}` : '—'}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Company Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6 max-w-2xl" data-testid="company-settings-tab">
              <div className="bg-white rounded-xl border border-[#1A3C34]/5 p-6 space-y-4">
                <h3 className="font-semibold text-[#1A3C34]">Të Dhënat e Kompanisë</h3>
                <div>
                  <Label>Emri i Kompanisë *</Label>
                  <Input value={companyForm.name} onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })} className="mt-1" data-testid="company-name-input" />
                </div>
                <div>
                  <Label>Industria *</Label>
                  <Input value={companyForm.industry} onChange={(e) => setCompanyForm({ ...companyForm, industry: e.target.value })} className="mt-1" data-testid="company-industry-input" placeholder="p.sh. Teknologji, Tregti, Shërbime" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Viti Fiskal Mbaron</Label>
                    <Select value={companyForm.fiscal_year_end} onValueChange={(v) => setCompanyForm({ ...companyForm, fiscal_year_end: v })}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['December','January','February','March','April','May','June','July','August','September','October','November'].map(m => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Monedha</Label>
                    <Select value={companyForm.currency} onValueChange={(v) => setCompanyForm({ ...companyForm, currency: v })}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['USD','EUR','GBP','ALL','CHF','RSD','MKD'].map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>NUI / NIPT (Numri Fiskal)</Label>
                  <Input value={companyForm.tax_id} onChange={(e) => setCompanyForm({ ...companyForm, tax_id: e.target.value })} className="mt-1" data-testid="company-taxid-input" placeholder="p.sh. 810123456" />
                </div>
                <div>
                  <Label>Përshkrim</Label>
                  <Input value={companyForm.description} onChange={(e) => setCompanyForm({ ...companyForm, description: e.target.value })} className="mt-1" placeholder="Përshkrim i shkurtër i kompanisë" />
                </div>
                <Button onClick={handleSaveCompany} disabled={savingCompany} className="bg-[#1A3C34] text-white" data-testid="company-save-btn">
                  {savingCompany ? <Spinner className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Ruaj Ndryshimet
                </Button>
              </div>

              {/* Danger zone */}
              <div className="bg-[#E76F51]/5 rounded-xl border border-[#E76F51]/20 p-6">
                <h3 className="font-semibold text-[#E76F51] mb-1">Zona e Rrezikut</h3>
                <p className="text-sm text-[#1A3C34]/60 mb-4">Fshirja e kompanisë do të heqë përgjithmonë të gjitha pasqyrat dhe njohuritë e lidhura. Ky veprim nuk mund të zhbëhet.</p>
                <Button onClick={() => setShowDeleteConfirm(true)} variant="outline" className="border-[#E76F51]/40 text-[#E76F51] hover:bg-[#E76F51]/10" data-testid="company-delete-btn">
                  <Trash className="w-4 h-4 mr-2" />Fshi Kompaninë
                </Button>
              </div>
            </div>
          )}

        </div>
      </main>

      {/* Modals */}
      <AddStatementModal
        open={showAddStatement}
        onClose={() => setShowAddStatement(false)}
        onSuccess={handleStatementAdded}
        companyId={companyId}
        currency={company?.currency || 'USD'}
      />
      <UploadDocumentModal
        open={showUploadDocument}
        onClose={() => setShowUploadDocument(false)}
        onSuccess={handleDocumentUploaded}
        companyId={companyId}
      />

      {/* Delete Company Confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#E76F51]">Fshi Kompaninë?</DialogTitle>
            <DialogDescription>
              Jeni të sigurt që doni të fshini <span className="font-medium">{company?.name}</span>? Të gjitha pasqyrat dhe njohuritë do të fshihen përgjithmonë.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" className="flex-1" onClick={() => setShowDeleteConfirm(false)}>Anulo</Button>
            <Button className="flex-1 bg-[#E76F51] hover:bg-[#E76F51]/90 text-white" onClick={handleDeleteCompany} disabled={deletingCompany} data-testid="confirm-delete-company-btn">
              {deletingCompany ? <Spinner className="w-4 h-4 mr-2 animate-spin" /> : <Trash className="w-4 h-4 mr-2" />}
              Fshi
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyDetail;
